/**
 * Settings Tab Component - Customer-specific configuration and settings
 * Includes OTIF window settings, company information editing, customer config,
 * and supplier relationship management.
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import {
  Loader2, Settings, Clock, Info,
  Layers, UserCog, BarChart3,
} from 'lucide-react';
import { coreAPI, purchasingAPI, CustomerProfile, CustomerSupplierRelationship, CommodityGroup, CommodityType, Site, UserProfile as APIUserProfile } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { OtifSettings } from './types';
import { toast } from 'sonner';
import { SupplierRelationshipsSection } from './SupplierRelationshipsSection';

interface SettingsTabProps {
  customer: CustomerProfile;
  otifSettings: OtifSettings | null;
  otifLoading: boolean;
  otifSaving: boolean;
  isHelixAdmin: boolean;
  isSupplier: boolean;
  onOtifSettingsUpdate: (settings: Partial<OtifSettings>) => Promise<void>;
  setOtifSettings: React.Dispatch<React.SetStateAction<OtifSettings | null>>;
  onUpdateProfile: (data: Partial<CustomerProfile>) => Promise<void>;
}

export function SettingsTab({
  customer, otifSettings, otifLoading, otifSaving,
  isHelixAdmin, isSupplier, onOtifSettingsUpdate, setOtifSettings,
  onUpdateProfile,
}: SettingsTabProps) {
  const canEditSettings = isHelixAdmin || isSupplier;
  const { formatDate } = useFormatters();

  // Supplier Relationships state
  const [relationships, setRelationships] = useState<CustomerSupplierRelationship[]>([]);
  const [relLoading, setRelLoading] = useState(false);
  const [expandedRelId, setExpandedRelId] = useState<number | null>(null);
  const [relSaving, setRelSaving] = useState<number | null>(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [availableSites, setAvailableSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [assigning, setAssigning] = useState(false);

  // CR-4362: Commodity Settings state
  const [commodityGroups, setCommodityGroups] = useState<CommodityGroup[]>([]);
  const [commodityTypes, setCommodityTypes] = useState<CommodityType[]>([]);
  const [commoditySaving, setCommoditySaving] = useState(false);

  // CR-4363: Management Settings state
  const [customerUsers, setCustomerUsers] = useState<APIUserProfile[]>([]);
  const [managementSaving, setManagementSaving] = useState(false);

  const fetchCustomerUsers = useCallback(async () => {
    try {
      const res = await coreAPI.getCompanyUsers(customer.company);
      setCustomerUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err: unknown) {
      console.error('Error loading supplier users:', err);
    }
  }, [customer.company]);

  const updateManagement = async (data: Partial<CustomerProfile>) => {
    setManagementSaving(true);
    try {
      await onUpdateProfile(data);
      toast.success('Management settings updated');
    } catch {
      toast.error('Failed to update management settings');
    } finally {
      setManagementSaving(false);
    }
  };

  const fetchCommodities = useCallback(async () => {
    try {
      const [gRes, tRes] = await Promise.all([
        purchasingAPI.getCommodityGroups({ page_size: 100 }),
        purchasingAPI.getCommodityTypes({ page_size: 500 }),
      ]);
      const gData = gRes.data;
      const tData = tRes.data;
      setCommodityGroups(Array.isArray(gData) ? gData : (gData as Record<string, unknown>)['results'] as CommodityGroup[]);
      setCommodityTypes(Array.isArray(tData) ? tData : (tData as Record<string, unknown>)['results'] as CommodityType[]);
    } catch (err: unknown) {
      console.error('Error loading commodities:', err);
    }
  }, []);

  const updateCommodity = async (data: Partial<CustomerProfile>) => {
    setCommoditySaving(true);
    try {
      await onUpdateProfile(data);
      toast.success('Commodity settings updated');
    } catch {
      toast.error('Failed to update commodity settings');
    } finally {
      setCommoditySaving(false);
    }
  };

  const fetchRelationships = useCallback(async () => {
    setRelLoading(true);
    try {
      const res = await coreAPI.getCustomerRelationships({ customer: customer.company, page_size: 100 });
      const data = res.data;
      setRelationships(Array.isArray(data) ? data : (data as Record<string, unknown>)['results'] as CustomerSupplierRelationship[]);
    } catch (err: unknown) {
      console.error('Error loading relationships:', err);
    } finally {
      setRelLoading(false);
    }
  }, [customer.company]);

  useEffect(() => {
    fetchRelationships();
    fetchCommodities();
    fetchCustomerUsers();
  }, [fetchRelationships, fetchCommodities, fetchCustomerUsers]);

  const updateRelationship = async (id: number, data: Partial<CustomerSupplierRelationship>) => {
    setRelSaving(id);
    try {
      await coreAPI.updateCustomerRelationship(id, data);
      setRelationships((prev) => prev.map((r) => (r.id === id ? { ...r, ...data } : r)));
      toast.success('Relationship updated');
    } catch (err: unknown) {
      console.error('Error updating relationship:', err);
      toast.error('Failed to update relationship');
    } finally {
      setRelSaving(null);
    }
  };

  const openAssignDialog = async () => {
    try {
      const res = await coreAPI.getAccessibleSites();
      const sites: Site[] = Array.isArray(res.data) ? res.data : (res.data as Record<string, unknown>)['results'] as Site[];
      const existingSiteIds = new Set(relationships.map((r) => r.supplier));
      setAvailableSites(sites.filter((s) => !existingSiteIds.has(s.id)));
      setSelectedSiteId(null);
      setAssignDialogOpen(true);
    } catch (err: unknown) {
      console.error('Error loading sites:', err);
      toast.error('Failed to load sites');
    }
  };

  const assignSite = async () => {
    if (!selectedSiteId) return;
    setAssigning(true);
    try {
      const res = await coreAPI.createCustomerRelationship({ customer: customer.company, supplier: selectedSiteId });
      setRelationships((prev) => [...prev, res.data]);
      setAssignDialogOpen(false);
      toast.success('Supplier assigned');
    } catch (err: unknown) {
      console.error('Error assigning site:', err);
      toast.error('Failed to assign supplier');
    } finally {
      setAssigning(false);
    }
  };

  const removeRelationship = async (id: number) => {
    try {
      await coreAPI.deleteCustomerRelationship(id);
      setRelationships((prev) => prev.filter((r) => r.id !== id));
      setExpandedRelId(null);
      toast.success('Relationship removed');
    } catch (err: unknown) {
      console.error('Error removing relationship:', err);
      toast.error('Failed to remove relationship');
    }
  };


  return (
    <div className="space-y-6">
      {/* CR-4819: Company Information moved to Overview tab to remove duplication */}

      {/* Supplier Relationships */}
      <SupplierRelationshipsSection
        relationships={relationships} relLoading={relLoading} expandedRelId={expandedRelId}
        setExpandedRelId={setExpandedRelId} relSaving={relSaving}
        canEditSettings={canEditSettings}
        assignDialogOpen={assignDialogOpen} setAssignDialogOpen={setAssignDialogOpen}
        availableSites={availableSites} selectedSiteId={selectedSiteId}
        setSelectedSiteId={setSelectedSiteId} assigning={assigning}
        setRelationships={setRelationships} updateRelationship={updateRelationship}
        removeRelationship={removeRelationship} openAssignDialog={openAssignDialog}
        assignSite={assignSite}
      />

      {/* OTIF Window Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5" />OTIF Window Settings</CardTitle>
          <CardDescription>Configure On-Time In-Full delivery window tolerances for this customer</CardDescription>
        </CardHeader>
        <CardContent>
          {otifLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : otifSettings ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <label className="text-sm font-medium">Use Custom OTIF Window</label>
                  <p className="text-xs text-muted-foreground">Override company defaults with customer-specific OTIF tolerances</p>
                </div>
                <Switch checked={otifSettings.use_custom_otif_window} onCheckedChange={(checked) => { if (canEditSettings) { setOtifSettings({ ...otifSettings, use_custom_otif_window: checked }); onOtifSettingsUpdate({ use_custom_otif_window: checked }); } }} disabled={!canEditSettings || otifSaving} />
              </div>
              {otifSettings.use_custom_otif_window && (
                <div className="grid gap-4 md:grid-cols-2">
                  {[
                    { label: 'Days Early Allowed', field: 'otif_days_early_allowed' as const, hint: 'Deliveries this many days early are still considered on-time' },
                    { label: 'Days Late Allowed', field: 'otif_days_late_allowed' as const, hint: 'Deliveries this many days late are still considered on-time' },
                  ].map(({ label, field, hint }) => (
                    <div key={field} className="space-y-2">
                      <label className="text-sm font-medium">{label}</label>
                      <div className="flex items-center gap-2">
                        <input type="number" min="0" max="30" value={otifSettings[field]} onChange={(e) => { if (canEditSettings) { const value = parseInt(e.target.value) || 0; setOtifSettings({ ...otifSettings, [field]: value }); } }} onBlur={() => { if (canEditSettings) onOtifSettingsUpdate({ [field]: otifSettings[field] }); }} className="w-20 px-3 py-2 border rounded-md text-sm" disabled={!canEditSettings || otifSaving} />
                        <span className="text-sm text-muted-foreground">days</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{hint}</p>
                    </div>
                  ))}
                </div>
              )}
              {!otifSettings.use_custom_otif_window && (
                <div className="bg-muted/30 p-4 rounded-lg">
                  <div className="flex items-start gap-2"><Info className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" /><p className="text-sm text-muted-foreground">Using company default OTIF settings. Enable custom settings above to configure customer-specific tolerances.</p></div>
                </div>
              )}
              {otifSaving && (<div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Saving settings...</div>)}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground"><p>Unable to load OTIF settings</p></div>
          )}
        </CardContent>
      </Card>

      {/* CR-4362: Commodity Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Layers className="h-5 w-5" />Commodity Settings</CardTitle>
          <CardDescription>Assign commodity group and type classification for this customer</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Commodity Group</label>
              <select value={customer.commodity_group ?? ''} onChange={(e) => { const val = e.target.value ? parseInt(e.target.value) : null; updateCommodity({ commodity_group: val, commodity_type: null } as Partial<CustomerProfile>); }} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings || commoditySaving}>
                <option value="">None</option>
                {commodityGroups.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Commodity Type</label>
              <select value={customer.commodity_type ?? ''} onChange={(e) => { const val = e.target.value ? parseInt(e.target.value) : null; updateCommodity({ commodity_type: val } as Partial<CustomerProfile>); }} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings || commoditySaving || !customer.commodity_group}>
                <option value="">None</option>
                {commodityTypes.filter((t) => t.commodity_group === customer.commodity_group).map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
              </select>
            </div>
          </div>
          {commoditySaving && (<div className="flex items-center gap-2 text-sm text-muted-foreground mt-3"><Loader2 className="h-4 w-4 animate-spin" />Saving...</div>)}
        </CardContent>
      </Card>

      {/* CR-4363: Management Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><UserCog className="h-5 w-5" />Management Settings</CardTitle>
          <CardDescription>Assign commodity manager and customer development manager for this customer</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Commodity Manager</label>
              <select value={customer.commodity_manager ?? ''} onChange={(e) => { const val = e.target.value ? parseInt(e.target.value) : null; updateManagement({ commodity_manager: val } as Partial<CustomerProfile>); }} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings || managementSaving}>
                <option value="">Not assigned</option>
                {customerUsers.map((u) => (<option key={u.user.id} value={u.user.id}>{u.user.full_name || u.user.email}</option>))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Customer Development Manager</label>
              <select value={customer.customer_development_manager ?? ''} onChange={(e) => { const val = e.target.value ? parseInt(e.target.value) : null; updateManagement({ customer_development_manager: val } as Partial<CustomerProfile>); }} className="w-full px-3 py-2 border rounded-md text-sm bg-background" disabled={!canEditSettings || managementSaving}>
                <option value="">Not assigned</option>
                {customerUsers.map((u) => (<option key={u.user.id} value={u.user.id}>{u.user.full_name || u.user.email}</option>))}
              </select>
            </div>
          </div>
          {managementSaving && (<div className="flex items-center gap-2 text-sm text-muted-foreground mt-3"><Loader2 className="h-4 w-4 animate-spin" />Saving...</div>)}
        </CardContent>
      </Card>

      {/* CR-4365: KPI Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5" />KPI Settings</CardTitle>
          <CardDescription>Configure which KPI calculations include this customer</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { field: 'include_in_otif' as const, label: 'Include in OTIF', description: 'Include this customer in On-Time In-Full calculations' },
              { field: 'include_in_otd' as const, label: 'Include in OTD', description: 'Include this customer in On-Time Delivery calculations' },
            ].map(({ field, label, description }) => (
              <div key={field} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <label className="text-sm font-medium">{label}</label>
                  <p className="text-xs text-muted-foreground">{description}</p>
                </div>
                <Switch checked={customer[field]} onCheckedChange={async (checked) => { if (canEditSettings) { try { await onUpdateProfile({ [field]: checked } as Partial<CustomerProfile>); toast.success(`${label} ${checked ? 'enabled' : 'disabled'}`); } catch { toast.error(`Failed to update ${label}`); } } }} disabled={!canEditSettings} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Audit Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" />Audit Schedule</CardTitle>
          <CardDescription>Audit tracking and scheduling information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider">Last Audit Date</label>
              <p className="font-medium">{customer.last_audit_date ? formatDate(customer.last_audit_date) : 'No audit recorded'}</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider">Next Audit Date</label>
              <p className="font-medium">{customer.next_audit_date ? formatDate(customer.next_audit_date) : 'Not scheduled'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {!canEditSettings && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">You have view-only access to customer settings. Contact your administrator for modifications.</p>
          </div>
        </div>
      )}

    </div>
  );
}
